import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PaymentMethodDTO } from "@gymflow/types";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { ModalContext, useAuthenticatedUser } from "../../providers";
import { Button } from "../atoms";
import CustomTooltip from "../atoms/CustomTooltip";
import { DotBadge } from "../atoms/DotBadge";
import {
  CARD_BRAND_ICONS,
  PAYMENT_METHOD_TYPE_ICONS,
  WALLET_ICONS,
} from "../PaymentMethodIcons";
import { VerifyMicroDepositModal } from "./VerifyMicroDepositModal";

export function PaymentMethodDetails({
  paymentMethod,
  userMemberId,
}: {
  paymentMethod?: PaymentMethodDTO;
  userMemberId?: string;
}) {
  const auth = useAuthenticatedUser();
  const as = !auth.roles.includes("MEMBER") ? "staff" : "member";
  const { t } = useTranslation();
  const { stackModal } = useContext(ModalContext);
  if (!paymentMethod || !userMemberId) {
    return (
      <div className="flex size-full flex-row items-center justify-between"></div>
    );
  }
  return (
    <div className="flex size-full flex-row items-center justify-between">
      <div className="flex w-full flex-col items-start">
        <div className="dark:text-gray-0 text-sm font-bold text-gray-950">
          {paymentMethod.linkEmail ??
            t("common.endingIn", { last4Digits: paymentMethod.last4Digits })}
        </div>
        {paymentMethod.expMonth ? (
          <div className="text-sm font-normal text-gray-500">
            {t("common.expiry", {
              expiry: `${paymentMethod.expMonth}/${paymentMethod.expYear}`,
            })}
          </div>
        ) : paymentMethod.bankName ? (
          <div className="text-sm font-normal text-gray-500">
            {paymentMethod.bankName}
          </div>
        ) : undefined}
      </div>
      {paymentMethod.status === "ACTIVE"
        ? paymentMethod.walletType
          ? WALLET_ICONS[paymentMethod.walletType]
          : {
              ...PAYMENT_METHOD_TYPE_ICONS,
              CARD: CARD_BRAND_ICONS[paymentMethod.cardBrand ?? "unknown"],
            }[paymentMethod.type]
        : undefined}
      {paymentMethod.status === "PENDING" && (
        <div className="flex flex-row items-center gap-1">
          {as === "staff" && (
            <CustomTooltip
              message={t(`common.paymentMethods.pendingDescription`)}
            >
              <Button
                onClick={() => {
                  stackModal(
                    <VerifyMicroDepositModal
                      as="staff"
                      userMemberId={userMemberId}
                      paymentMethodId={paymentMethod.paymentMethodId}
                    />,
                  );
                }}
                intent="transparent"
                size="icon"
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Button>
            </CustomTooltip>
          )}
          <DotBadge size="small" intent="warning">
            {t("common.paymentMethods.pending")}
          </DotBadge>
        </div>
      )}
      {paymentMethod.status === "INVALID" && (
        <div className="flex flex-row items-center gap-1">
          <CustomTooltip
            message={t(
              `common.paymentMethods.error_codes.${
                paymentMethod.invalidateReason
              }`,
            )}
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </CustomTooltip>
          <DotBadge size="small" intent="error">
            {t("common.paymentMethods.invalid")}
          </DotBadge>
        </div>
      )}
    </div>
  );
}
